import React, { Fragment, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'

import * as dates from 'date-arithmetic'
import { Calendar, Views, Navigate, momentLocalizer } from 'react-big-calendar'
import TimeGrid from 'react-big-calendar/lib/TimeGrid'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
// import './calendar-styles.css'
import CalendarSlot from './CalendarSlot'
import { reinterpretUtcAsLocalTime } from '../../utils/calendarUtils'
import { format } from 'date-fns'
import './calendar-styles.css'
import { Button } from '../../design-system/index'


function FiveDayWeek({
  date,
  localizer,
  max = localizer.endOf(new Date(), 'day'),
  min = localizer.startOf(new Date(), 'day'),
  scrollToTime = localizer.startOf(new Date(), 'day'),
  ...props
}) {
  const currRange = useMemo(
    () => FiveDayWeek.range(date, { localizer }),
    [date, localizer]
  )

return (
  <TimeGrid
    date={date}
    eventOffset={15}
    localizer={localizer}
    max={max}
    min={min}
    range={currRange}
    scrollToTime={scrollToTime}
    {...props}
  />
)
}

FiveDayWeek.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  localizer: PropTypes.object,
  max: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
}
FiveDayWeek.range = (date, { localizer }) => {
  const start = dates.subtract(date, 2, 'day')
  const end = dates.add(date, 2, 'day')
  start.setHours(0, 0, 0, 0)
  end.setHours(23, 59, 59, 999)

  let current = start
  const range = []

  while (localizer.lte(current, end, 'day')) {
    range.push(current)
    current = localizer.add(current, 1, 'day')
  }

  return range
}

FiveDayWeek.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -5, 'day')

    case Navigate.NEXT:
      return localizer.add(date, 5, 'day')

    default:
      return date
  }
}

FiveDayWeek.title = (date) => {
  return `Week: ${dates.subtract(date, 2, 'day').toLocaleDateString()} - ${dates.add(date, 2, 'day').toLocaleDateString()}`
}


function ThreeDayWeek({
  date,
  localizer,
  max = localizer.endOf(new Date(), 'day'),
  min = localizer.startOf(new Date(), 'day'),
  scrollToTime = localizer.startOf(new Date(), 'day'),
  ...props
}) {
  const currRange = useMemo(
    () => ThreeDayWeek.range(date, { localizer }),
    [date, localizer]
  )

  return (
    <TimeGrid
      date={date}
      eventOffset={15}
      localizer={localizer}
      max={max}
      min={min}
      range={currRange}
      scrollToTime={scrollToTime}
      {...props}
    />
  )
}

ThreeDayWeek.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  localizer: PropTypes.object,
  max: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
}

ThreeDayWeek.range = (date, { localizer }) => {
  const start = dates.subtract(date, 1, 'day')
  const end = dates.add(date, 1, 'day')
  start.setHours(0, 0, 0, 0)
  end.setHours(23, 59, 59, 999)

  let current = start
  const range = []

  while (localizer.lte(current, end, 'day')) {
    range.push(current)
    current = localizer.add(current, 1, 'day')
  }

  return range
}

ThreeDayWeek.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -3, 'day')

    case Navigate.NEXT:
      return localizer.add(date, 3, 'day')

    default:
      return date
  }
}

ThreeDayWeek.title = (date) => {
  return `Week: ${dates.subtract(date, 1, 'day').toLocaleDateString()} - ${dates.add(date, 1, 'day').toLocaleDateString()}`
}

// Two Day view component for mobile
function TwoDayView({
  date,
  localizer,
  max = localizer.endOf(new Date(), 'day'),
  min = localizer.startOf(new Date(), 'day'),
  scrollToTime = localizer.startOf(new Date(), 'day'),
  ...props
}) {
  const currRange = useMemo(
    () => TwoDayView.range(date, { localizer }),
    [date, localizer]
  )

  return (
    <TimeGrid
      date={date}
      eventOffset={15}
      localizer={localizer}
      max={max}
      min={min}
      range={currRange}
      scrollToTime={scrollToTime}
      {...props}
    />
  )
}

TwoDayView.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  localizer: PropTypes.object,
  max: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
}

TwoDayView.range = (date, { localizer }) => {
  const start = dates.subtract(date, 1, 'day') // Start with current date
  const end = date // Add one more day
  start.setHours(0, 0, 0, 0)
  end.setHours(23, 59, 59, 999)

  let current = start
  const range = []

  while (localizer.lte(current, end, 'day')) {
    range.push(current)
    current = localizer.add(current, 1, 'day')
  }

  return range
}

TwoDayView.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -1, 'day')

    case Navigate.NEXT:
      return localizer.add(date, 1, 'day')

    default:
      return date
  }
}

TwoDayView.title = (date) => {
  return `${date.toLocaleDateString()} - ${dates.add(date, 1, 'day').toLocaleDateString()}`
}

export default function CalendarCard({ tasksByDay = {}, renderTask, fetchAllDayTasks, calendarDays, setCalendarDays, onTaskDrop, onViewChange }) {
  const localizer = momentLocalizer(moment);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isSimplifiedView, setIsSimplifiedView] = useState(false);
  const [mergedEvents, setMergedEvents] = useState([]);
  const [proximityThreshold, setProximityThreshold] = useState(15); // Default 15 minutes
  
  // Create current time for scrolling
  const currentTime = useMemo(() => {
    const now = new Date();
    // Create a new date object with current time
    return now;
  }, []);
  
  // Check for mobile view on component mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Ensure view stays on appropriate view when in mobile mode
  useEffect(() => {
    if (isMobileView && document.querySelector('.rbc-btn-group')) {
      // Force custom view in mobile mode
      const buttons = document.querySelectorAll('.rbc-btn-group button');
      if (buttons && buttons.length > 0) {
        const customButton = buttons[1]; // Use the middle button for our custom view
        if (customButton) {
          customButton.click();
        }
      }
    }
  }, [isMobileView]);
  
  const { views } = useMemo(
    () => ({
      views: {
        month: true,
        threeweekdays: isMobileView ? TwoDayView : ThreeDayWeek,
        fiveweekdays:FiveDayWeek,
        week:true,
        day: true,
      },
    }),
    [isMobileView]
  )

  // Use the current date instead of hardcoded date
  const currentDate = new Date();

  // Convert tasksByDay to events for react-big-calendar
  const events = useMemo(() => {
    const allEvents = [];
    
    Object.keys(tasksByDay).forEach(dateKey => {
      const tasksForDay = tasksByDay[dateKey] || [];
      
      tasksForDay.forEach(task => {
        // Include all tasks, even those without time info
        let startDateTime, endDateTime;
        
        try {
          // For tasks with time info
          if (task.start_time) {
            // Handle start time directly with native Date objects
            if (task.start_time.includes('T')) {
              // For ISO format, parse directly
              startDateTime = reinterpretUtcAsLocalTime(task.start_time);
            } else {
              // For time-only format (HH:MM)
              const [hours, minutes] = task.start_time.split(':').map(num => parseInt(num, 10));
              startDateTime = new Date(dateKey);
              startDateTime.setHours(hours, minutes, 0, 0);
            }
            
            // Handle end time
            if (task.end_time) {
              if (task.end_time.includes('T')) {
                // For ISO format
                endDateTime = reinterpretUtcAsLocalTime(task.end_time);
              } else {
                // For time-only format (HH:MM)
                const [hours, minutes] = task.end_time.split(':').map(num => parseInt(num, 10));
                endDateTime = new Date(dateKey);
                endDateTime.setHours(hours, minutes, 0, 0);
              }
            } else {
              // Default to 1 hour duration if no end time
              endDateTime = new Date();
            //   endDateTime.setHours(endDateTime.getHours() + 1);
            }
          } else {
            // For tasks without time info, set them as all-day events
            startDateTime = new Date(dateKey);
            startDateTime.setHours(0, 0, 0, 0);
            endDateTime = new Date(dateKey);
            endDateTime.setHours(23, 59, 59, 999);
          }
          
          // Apply the timezone adjustment
          // const adjustedStart = task.start_time ? dates.subtract(startDateTime, 5.5, 'hours') : startDateTime;
          // const adjustedEnd = task.end_time ? dates.subtract(endDateTime, 5.5, 'hours') : endDateTime;
          
          allEvents.push({
            id: task.id,
            title: task.title,
            start: startDateTime,
            end: endDateTime,
            task: task, // Store the original task for rendering
            allDay: !task.start_time // Mark as all-day if no specific time
          });
        } catch (e) {
          console.error(`Error processing task ${task.id}:`, e);
        }
      });
    });
    
    return allEvents;
  }, [tasksByDay]);

  // Generate merged events for simplified view
  useEffect(() => {
    if (isSimplifiedView) {
      // Group events by date
      const eventsByDate = {};
      
      events.forEach(event => {
        if (event.allDay) return; // Skip all-day events
        
        const dateStr = event.start.toISOString().split('T')[0];
        if (!eventsByDate[dateStr]) {
          eventsByDate[dateStr] = [];
        }
        eventsByDate[dateStr].push(event);
      });
      
      // For each date, merge overlapping events
      const mergedEventsList = [];
      
      Object.keys(eventsByDate).forEach(dateStr => {
        const dateEvents = eventsByDate[dateStr];
        
        // Sort events by start time
        dateEvents.sort((a, b) => a.start - b.start);
        
        // Merge overlapping events and events with small gaps
        const merged = [];
        let currentMerged = null;
        
        dateEvents.forEach(event => {
          if (!currentMerged) {
            currentMerged = { ...event };
          } else {
            // Calculate gap between current merged block end and next event start (in minutes)
            const gapMinutes = (event.start - currentMerged.end) / (1000 * 60);
            
            // If events overlap or the gap is within proximity threshold
            if (event.start <= currentMerged.end || gapMinutes <= proximityThreshold) {
              // Extend the end time if needed
              if (event.end > currentMerged.end) {
                currentMerged.end = event.end;
              }
              // Add task IDs to the merged event
              currentMerged.taskIds = [...(currentMerged.taskIds || [currentMerged.id]), event.id];
              currentMerged.tasks = [...(currentMerged.tasks || [currentMerged.task]), event.task];
            } else {
              // Non-overlapping event with gap > threshold - add the current merged event and start a new one
              merged.push(currentMerged);
              currentMerged = { ...event };
            }
          }
        });
        
        // Add the last merged event
        if (currentMerged) {
          merged.push(currentMerged);
        }
        
        mergedEventsList.push(...merged);
      });
      
      setMergedEvents(mergedEventsList);
    }
  }, [events, isSimplifiedView, proximityThreshold]);

  // Update calendar days when view changes
  const handleRangeChange = (range) => {
    if (Array.isArray(range)) {
      // For week and day views
      const newDays = range.map(date => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth is 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      });
      setCalendarDays(newDays);
      
      // Fetch tasks for these days
      if (fetchAllDayTasks) {
        fetchAllDayTasks(newDays);
      }
    } else if (range.start && range.end) {
      // For month view
      const days = [];
      let current = new Date(range.start);
      
      while (current <= range.end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0'); // getMonth is 0-indexed
        const day = String(current.getDate()).padStart(2, '0');
        days.push(`${year}-${month}-${day}`);
        current.setDate(current.getDate() + 1);
      }
      
      setCalendarDays(days);
      
      // Fetch tasks for these days
      if (fetchAllDayTasks) {
        fetchAllDayTasks(days);
      }
    }
  };

  // Handle view change events
  const handleViewChange = (view) => {
    if (onViewChange) {
      onViewChange(view);
    }
  };

  // Calculate the percentage of day that is blocked for each day
  const calculateDayBlockedPercentage = (date) => {
    const dateString = date.toISOString().split('T')[0];
    const tasksForDay = tasksByDay[dateString] || [];
    
    // Skip if no tasks for this day
    if (tasksForDay.length === 0) return 0;
    
    // Count only tasks with time info
    const tasksWithTime = tasksForDay.filter(task => task.start_time && task.end_time);
    if (tasksWithTime.length === 0) return 0;
    
    // Calculate total minutes in business hours (9am to 6pm = 9 hours = 540 minutes)
    const businessHoursMinutes = 9 * 60;
    
    if (isSimplifiedView && mergedEvents.length > 0) {
      // Use merged events for simplified view
      const mergedEventsForDay = mergedEvents.filter(
        event => event.start.toISOString().split('T')[0] === dateString
      );
      
      if (mergedEventsForDay.length === 0) return 0;
      
      // Track occupied minutes
      const occupiedMinutes = new Array(24 * 60).fill(false);
      
      mergedEventsForDay.forEach(event => {
        const startMinute = event.start.getHours() * 60 + event.start.getMinutes();
        const endMinute = event.end.getHours() * 60 + event.end.getMinutes();
        
        // Mark occupied minutes
        for (let i = startMinute; i < endMinute; i++) {
          occupiedMinutes[i] = true;
        }
      });
      
      // Count occupied minutes during business hours (9am to 6pm)
      const businessHoursStart = 9 * 60;
      const businessHoursEnd = 18 * 60;
      let occupiedBusinessMinutes = 0;
      
      for (let i = businessHoursStart; i < businessHoursEnd; i++) {
        if (occupiedMinutes[i]) occupiedBusinessMinutes++;
      }
      
      // Calculate percentage
      return Math.round((occupiedBusinessMinutes / businessHoursMinutes) * 100);
    }
    
    // Use original tasks for normal view
    // Track occupied time slots to avoid double counting overlaps
    const occupiedMinutes = new Array(24 * 60).fill(false);
    
    tasksWithTime.forEach(task => {
      let startMinute = 0;
      let endMinute = 0;
      
      try {
        if (task.start_time.includes('T')) {
          const startDate = new Date(task.start_time);
          startMinute = startDate.getHours() * 60 + startDate.getMinutes();
        } else {
          const [hours, minutes] = task.start_time.split(':').map(num => parseInt(num, 10));
          startMinute = hours * 60 + minutes;
        }
        
        if (task.end_time.includes('T')) {
          const endDate = new Date(task.end_time);
          endMinute = endDate.getHours() * 60 + endDate.getMinutes();
        } else {
          const [hours, minutes] = task.end_time.split(':').map(num => parseInt(num, 10));
          endMinute = hours * 60 + minutes;
        }
        
        // Mark occupied minutes
        for (let i = startMinute; i < endMinute; i++) {
          occupiedMinutes[i] = true;
        }
      } catch (e) {
        console.error('Error calculating task time:', e);
      }
    });
    
    // Count occupied minutes during business hours (9am to 6pm)
    const businessHoursStart = 9 * 60;
    const businessHoursEnd = 18 * 60;
    let occupiedBusinessMinutes = 0;
    
    for (let i = businessHoursStart; i < businessHoursEnd; i++) {
      if (occupiedMinutes[i]) occupiedBusinessMinutes++;
    }
    
    // Calculate percentage
    return Math.round((occupiedBusinessMinutes / businessHoursMinutes) * 100);
  };

  // Custom component to show day utilization in column header
  const DayColumnHeader = ({ date, label }) => {
    const percentage = calculateDayBlockedPercentage(date);
    let percentageClass = 'day-percentage day-percentage-low';
    
    if (percentage > 75) {
      percentageClass = 'day-percentage day-percentage-high';
    } else if (percentage > 50) {
      percentageClass = 'day-percentage day-percentage-medium';
    }
    
    return (
      <div className="rbc-day-column-header">
        <div className="text-center font-medium">{label}</div>
        <div className="text-xs text-center">
          <span className={percentageClass}>
            {percentage}% blocked
          </span>
        </div>
      </div>
    );
  };

  // Custom event component for simplified view
  const SimplifiedEvent = ({ event }) => {
    if (!event.task && !event.tasks) return null;
    
    // For merged events, show the number of tasks in the tooltip
    const taskCount = event.tasks ? event.tasks.length : 1;
    let tooltipText = '';
    
    if (event.tasks) {
      // Calculate if this is a merged block with gaps
      const hasMergedGaps = event.tasks && event.tasks.length > 1;
      
      // Create tooltip showing time range and task count
      tooltipText = `${taskCount} tasks from ${format(event.start, 'h:mm a')} - ${format(event.end, 'h:mm a')}`;
      
      // Add information about merged gaps if applicable
      if (hasMergedGaps) {
        tooltipText += ` (merged with ${proximityThreshold}min gap threshold)`;
      }
    } else {
      tooltipText = event.title;
    }
    
    return (
      <div className="rbc-event-content h-full">
        <div 
          className="h-full w-full rounded-md flex items-center justify-center"
          style={{ 
            backgroundColor: '#3b82f6',
            opacity: 0.7,
            border: 'none',
          }}
          title={tooltipText}
        >
          {taskCount > 1 && (
            <span className="text-white font-medium text-xs px-1">
              {taskCount} tasks
            </span>
          )}
        </div>
      </div>
    );
  };

  // Custom event component that uses renderTask
  const CustomEvent = ({ event }) => {
    if (!event.task) return null;
    
    // Determine task size based on duration
    const duration = (event.end - event.start) / (1000 * 60); // Duration in minutes
    let taskSize = { type: "normal" };
    
    if (duration <= 30) {
      taskSize = { type: "extraSmall" };
    } else if (duration <= 60) {
      taskSize = { type: "small" };
    } else if (duration >= 120) {
      taskSize = { type: "medium" };
    }
    
    return (
      <div className="rbc-event-content">
        {renderTask(event.task, -1, false, taskSize)}
      </div>
    );
  };

  // Custom time slot wrapper component that uses our CalendarSlot for drop handling
  const TimeSlotWrapper = ({ children, value }) => {
    // Extract date part for the current slot
    const slotDate = new Date(value);
    const dateOnly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
    
    return (
      <CalendarSlot 
        date={dateOnly} 
        time={slotDate}
        onTaskDrop={onTaskDrop}
      >
        {children}
      </CalendarSlot>
    );
  };

  // Custom date cell wrapper for month view drops
  const DateCellWrapper = ({ children, value }) => {
    return (
      <CalendarSlot
        date={value}
        onTaskDrop={onTaskDrop}
      >
        {children}
      </CalendarSlot>
    );
  };

  // Custom component for the date cell header in month view
  const MonthDateHeader = ({ label, date, isOffRange }) => {
    // Format date as yyyy-MM-dd to match tasksByDay keys
    const dateKey = date.toISOString().split('T')[0];
    const tasksForDay = tasksByDay[dateKey] || [];
    const extraTasksCount = tasksForDay.length > 1 ? tasksForDay.length - 1 : 0;

    return (
      <div className="rbc-date-header-content relative">
        {/* Default date number */}
        <span>{label}</span>
        {/* Display +X more badge if applicable */}
        {!isOffRange && extraTasksCount > 0 && (
          <div className="task-count-badge">
            +{extraTasksCount} more
          </div>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <div className="calendar-container flex flex-1 overflow-hidden" style={{ width: '100%', maxWidth: '100%' }}>
        <Calendar
          defaultDate={currentDate}
          defaultView={isMobileView ? views.day : "threeweekdays"}
          localizer={localizer}
          events={isSimplifiedView ? mergedEvents : events}
          views={views}
          step={30}
          dayLayoutAlgorithm="no-overlap"
          showMultiDayTimes
          timeslots={1}
          toolbar={true}
          scrollToTime={currentTime}
          formats={{
            eventTimeRangeFormat: () => '', // Hide time display above task cards
            eventTimeRangeEndFormat: () => '',
            eventTimeRangeStartFormat: () => ''
          }}
          components={{
            toolbar: props => <CustomToolbar 
              {...props} 
              isMobileView={isMobileView} 
              onViewChange={handleViewChange} 
              isSimplifiedView={isSimplifiedView}
              setIsSimplifiedView={setIsSimplifiedView}
              proximityThreshold={proximityThreshold}
              setProximityThreshold={setProximityThreshold}
            />,
            event: isSimplifiedView ? SimplifiedEvent : CustomEvent,
            timeSlotWrapper: TimeSlotWrapper,
            dateCellWrapper: DateCellWrapper,
            month: {
              dateHeader: MonthDateHeader,
            },
            timeGutterHeader: () => <div className="rbc-time-header-gutter-content">Time</div>,
            dayHeader: DayColumnHeader
          }}
          onRangeChange={handleRangeChange}
          onView={handleViewChange}
          eventPropGetter={(event) => {
            // For simplified view, use a simpler style
            if (isSimplifiedView) {
              return {
                style: {
                  backgroundColor: '#3b82f6',
                  borderRadius: '4px',
                  opacity: 0.7,
                  border: 'none',
                  padding: 0,
                  margin: 0,
                },
              };
            }
            
            // For normal view, use the existing style
            return {
              style: {
                backgroundColor: 'transparent',
                borderRadius: '4px',
                color: 'black',
                border: 'none',
                padding: 0,
                margin: 0,
              },
            };
          }}
          className={`full-width-calendar ${isSimplifiedView ? 'simplified-view' : ''}`}
        />
      </div>
    </Fragment>
  )
}

// Custom toolbar component to match the design
function CustomToolbar({ label, onNavigate, onView, views, isMobileView, onViewChange, isSimplifiedView, setIsSimplifiedView, proximityThreshold, setProximityThreshold }) {
  const navigate = useNavigate();
  
  const handleViewSelect = (view) => {
    onView(view);
    if (onViewChange) {
      onViewChange(view);
    }
  };

  const handleProximityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setProximityThreshold(value);
    }
  };

  return (
    <div className="rbc-toolbar">
        <Button leftIcon={<FaArrowLeft />} variant="accent-outline" onClick={() => navigate('/')}>Back</Button>

      <span className="rbc-btn-group">
       
        <button type="button" onClick={() => onNavigate(Navigate.TODAY)}>Today</button>
        <button type="button" onClick={() => onNavigate(Navigate.PREVIOUS)}>Back</button>
        <button type="button" onClick={() => onNavigate(Navigate.NEXT)}>Next</button>
      </span>
      <span className="rbc-toolbar-label">{label}</span>
      <span className="rbc-btn-group">
        {!isMobileView && views.map(view => (
          <button
            key={view}
            type="button"
            onClick={() => handleViewSelect(view)}
          >
            {view === 'month' ? 'Month' : view === 'threeweekdays' ? (isMobileView ? '2 Day View' : '3 Day Week') :view==='fiveweekdays' ? '5 Day Week' : view === 'week' ? 'Week' : 'Day'}
          </button>
        ))}
        <button 
          type="button" 
          onClick={() => setIsSimplifiedView(!isSimplifiedView)}
          className={`simplified-toggle ${isSimplifiedView ? 'active' : ''}`}
        >
          {isSimplifiedView ? 'Detailed View' : 'Simplified View'}
        </button>
        
        {isSimplifiedView && (
          <div className="proximity-control">
            <label htmlFor="proximityThreshold" className="text-sm mr-1">
              Merge gap:
            </label>
            <input 
              id="proximityThreshold"
              type="number" 
              min="0" 
              max="120"
              value={proximityThreshold} 
              onChange={handleProximityChange}
              className="proximity-input"
            />
            <span className="text-sm ml-1">min</span>
          </div>
        )}
      </span>
    </div>
  );
}

// Update PropTypes to include the new props
CalendarCard.propTypes = {
  tasksByDay: PropTypes.object,
  renderTask: PropTypes.func,
  fetchAllDayTasks: PropTypes.func,
  calendarDays: PropTypes.array,
  setCalendarDays: PropTypes.func,
  onTaskDrop: PropTypes.func,
  onViewChange: PropTypes.func
}